import axios from 'axios';
import api from './client';

import { API_BASE_URL } from '../config';

const API_URL = API_BASE_URL;
const BASE_URL = API_URL.replace('/api', '');

export interface ReportTemplate {
  id: string;
  name: string;
  description?: string;
  template_type: string;
  format: string;
  config: any;
}

export interface Report {
  report_id: string;
  project_id: string;
  report_type: string;
  file_path: string;
  download_url: string;
  generated_at: string;
  status: string;
}

export interface ReportPreview {
  project_health: string;
  key_milestones: Array<{
    name: string;
    status: string;
  }>;
  critical_issues: Array<{
    type: string;
    count: number;
    status: string;
  }>;
  template_name?: string;
  project_name?: string;
  has_logo?: boolean;
  color_scheme?: string;
  logo_position?: string;
  date_range?: {
    enabled: boolean;
    start_date: string | null;
    end_date: string | null;
  };
  sample_activities?: Array<{id: string, name: string, status: string}>;
}

export interface CustomSettings {
  color_scheme: string;
  logo_position: string;
  include_sections: string[];
  grouping: string;
  sorting: string;
  filters: any;
  show_charts: boolean;
  chart_type: string;
}

// Default template constants
export const SCHEDULE_REPORT_TEMPLATES = [
  { id: 'schedule-breakdown', name: 'Schedule Breakdown', template_type: 'schedule' },
  { id: 'wbs-tree', name: 'WBS Tree Report', template_type: 'schedule' },
  { id: 'calendar-mapping', name: 'Calendar Mapping', template_type: 'schedule' }
];

export const PERFORMANCE_REPORT_TEMPLATES = [
  { id: 'planned-vs-actual', name: 'Planned vs Actual', template_type: 'performance' },
  { id: 'slippage-analysis', name: 'Slippage Analysis', template_type: 'performance' },
  { id: 'progress-snapshot', name: 'Progress Snapshot', template_type: 'performance' }
];

export const RESOURCE_REPORT_TEMPLATES = [
  { id: 'resource-allocation', name: 'Resource Allocation', template_type: 'resource' },
  { id: 'resource-utilization', name: 'Resource Utilization', template_type: 'resource' }
];

export const SUMMARY_REPORT_TEMPLATES = [
  { id: 'executive-summary', name: 'Executive Summary', template_type: 'summary' }
];

// Convert template IDs to report types
const TEMPLATE_TO_REPORT_TYPE: Record<string, string> = {
  'schedule-breakdown': 'schedule_breakdown',
  'wbs-tree': 'wbs_tree',
  'calendar-mapping': 'calendar_mapping',
  'planned-vs-actual': 'planned_vs_actual',
  'slippage-analysis': 'slippage_analysis',
  'progress-snapshot': 'progress_snapshot',
  'resource-allocation': 'resource_allocation',
  'resource-utilization': 'resource_utilization',
  'executive-summary': 'executive_summary'
};

// Reports API client methods
export const getReportTemplates = async (projectId: string): Promise<ReportTemplate[]> => {
  try {
    const response = await api.get(`${API_URL}/reports/templates`, {
      params: { project_id: projectId }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch report templates:', error);
    return [];
  }
};

export const generateReport = async (
  templateId: string,
  projectId: string,
  startDate?: string,
  endDate?: string,
  useDateRange: boolean = false,
  customSettings?: CustomSettings
): Promise<Report> => {
  try {
    // Map template ID to report type
    const reportType = TEMPLATE_TO_REPORT_TYPE[templateId] || templateId;
    
    // Prepare request payload
    const payload: any = {
      project_id: projectId,
      report_type: reportType,
      format: 'pdf'
    };
    
    // Add date range if needed
    if (useDateRange && startDate && endDate) {
      payload.date_range = {
        start: startDate,
        end: endDate
      };
    }
    
    // Add custom settings
    if (customSettings) {
      // Add grouping if present
      if (customSettings.grouping && customSettings.grouping !== 'none') {
        payload.grouping = customSettings.grouping;
      }
      
      // Add sorting if present
      if (customSettings.sorting) {
        payload.sorting = customSettings.sorting;
      }
      
      // Add show_charts flag
      if (customSettings.show_charts !== undefined) {
        payload.show_charts = customSettings.show_charts;
      }
      
      // Add chart_type if charts are enabled
      if (customSettings.show_charts && customSettings.chart_type) {
        payload.chart_type = customSettings.chart_type;
      }
      
      // Add report-specific settings
      if (reportType === 'slippage_analysis') {
        payload.min_delay_days = 1;
        payload.critical_only = false;
        payload.include_recommendations = true;
      }
      
      if (reportType === 'wbs_tree') {
        payload.include_details = true;
      }
      
      if (reportType === 'progress_snapshot') {
        // For progress snapshot, always set a default grouping if none is provided
        if (!payload.grouping) {
          payload.grouping = 'wbs';
        }
      }
    }
    
    console.log('Generating report with payload:', JSON.stringify(payload, null, 2));
    
    try {
      const response = await api.post(`${API_URL}/reports/generate`, payload);
      return {
        ...response.data,
        status: 'completed',
        generated_at: new Date().toISOString()
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error status:', error.response.status);
        
        // Handle different error types
        if (error.response.status === 422) {
          throw new Error(`Validation error: ${
            error.response.data?.detail || 'Invalid input data'
          }`);
        } else if (error.response.status === 404) {
          throw new Error(`Resource not found: ${
            error.response.data?.detail || 'Project or template not found'
          }`);
        } else {
          throw new Error(`Error ${error.response.status}: ${
            error.response.data?.detail || error.message
          }`);
        }
      }
      throw error;
    }
  } catch (error) {
    console.error('Failed to generate report:', error);
    throw error;
  }
};

export const getReportPreview = async (templateId: string, projectId: string): Promise<string> => {
  try {
    const response = await api.get(`${API_URL}/reports/preview/${templateId}`, {
      params: { project_id: projectId }
    });
    return response.data.preview_url;
  } catch (error) {
    console.error('Failed to get report preview:', error);
    return '';
  }
};

export const getReport = async (reportId: string): Promise<Report> => {
  const response = await api.get(`${API_URL}/reports/${reportId}`);
  const report = response.data;
  
  // Add the full base URL to the download_url if it's a relative path
  if (report.download_url && report.download_url.startsWith('/')) {
    report.download_url = `${BASE_URL}${report.download_url}`;
  }
  
  return report;
};

export const downloadReport = async (reportId: string): Promise<Blob> => {
  const response = await api.get(`${API_URL}/reports/${reportId}/download`, {
    responseType: 'blob'
  });
  return response.data;
};

export const uploadLogo = async (projectId: string, logoFile: File): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append('logo', logoFile);
    
    const response = await api.post(`${API_URL}/reports/upload-logo/${projectId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to upload logo:', error);
    throw error;
  }
};

export const deleteLogo = async (projectId: string): Promise<any> => {
  try {
    const response = await api.delete(`${API_URL}/reports/delete-logo/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting logo:', error);
    throw error;
  }
};

// Execution history types
export interface ReportExecutionSummary {
  execution_id: string;
  /** Template ID for preview/download (e.g. dcma:v1) */
  template_id: string;
  template_name: string;
  template_version: string;
  project_id: string;
  scenario_id?: string | null;
  parameters_hash?: string | null;
  status: string;
  progress: number;
  stage?: string | null;
  created_at?: string | null;
  created_by: string;
  artifact_pdf_url?: string | null;
  artifact_preview_url?: string | null;
  error_code?: string | null;
  error_user_message?: string | null;
}

export interface ReportExecutionsResponse {
  executions: ReportExecutionSummary[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface GetReportExecutionsParams {
  page?: number;
  page_size?: number;
  project_id?: string;
  template_id?: string;
  status?: string;
  created_by?: string;
  created_after?: string;
  created_before?: string;
}

/**
 * Get report executions with pagination and filtering
 */
export const getReportExecutions = async (
  params: GetReportExecutionsParams = {}
): Promise<ReportExecutionsResponse> => {
  try {
    const response = await api.get(`${API_URL}/v1/reports/executions`, {
      params: {
        page: params.page || 1,
        page_size: params.page_size || 25,
        ...(params.project_id && { project_id: params.project_id }),
        ...(params.template_id && { template_id: params.template_id }),
        ...(params.status && { status: params.status }),
        ...(params.created_by && { created_by: params.created_by }),
        ...(params.created_after && { created_after: params.created_after }),
        ...(params.created_before && { created_before: params.created_before }),
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch report executions:', error);
    throw error;
  }
}; 